import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { SparklesIcon, FileTextIcon, BookmarkIcon, MessageSquareIcon, UserIcon, BriefcaseIcon, ShoppingBagIcon, LifeBuoyIcon, LockIcon } from 'lucide-react';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
type NavItem = {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: boolean;
};
export function Sidebar() {
  const {
    hub
  } = useBusiness();
  const {
    isAuthenticated,
    openLogin,
    requireAuth
  } = useAuth();
  const navigate = useNavigate();
  const navItems: NavItem[] = [{
    to: '/app',
    label: 'Discover',
    icon: SparklesIcon
  }, {
    to: '/requests',
    label: 'Requests',
    icon: FileTextIcon
  }, {
    to: '/saved',
    label: 'Saved',
    icon: BookmarkIcon
  }, {
    to: '/messages',
    label: 'Messages',
    icon: MessageSquareIcon
  },
  // Business Hub is always available to signed-in users; My Shop appears
  // once the hub has been activated.
  {
    to: '/business',
    label: 'Business Hub',
    icon: BriefcaseIcon,
    badge: hub?.isNew
  } as NavItem, ...(hub?.active ? [{
    to: `/shop/${hub.shopSlug || 'shop'}`,
    label: 'My Shop',
    icon: ShoppingBagIcon
  } as NavItem] : []), {
    to: '/profile',
    label: 'Profile',
    icon: UserIcon
  }];
  return <aside className="app-fill-screen hidden lg:flex lg:flex-col w-64 bg-white border-r border-slate-100 h-screen sticky top-0" data-id="element-2974" data-mp="29o6pbfgl1rrt">
      <div className="p-6" data-id="element-2975" data-mp="1vp70a6p5rav4">
        <Logo size="md" data-id="element-2976" data-mp="20ojs75uvz84k" />
      </div>

      <nav className="flex-1 px-4 space-y-1 overflow-y-auto" data-id="element-2977" data-mp="20niaiijd7ndv">
        {navItems.map(item => {
        const locked = !isAuthenticated && item.to !== '/app';
        if (locked) {
          return <button key={item.to} onClick={() => requireAuth(() => navigate(item.to))} title="Sign in to access" className="group relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2978" data-mp="pazfrfa1e6d0">
                <item.icon className="w-5 h-5" data-id="element-2979" data-mp="2h6o3hyzelur4" />
                <span className="flex-1 text-left" data-id="element-2980" data-mp="2d7ytmgdkqvc8">{item.label}</span>
                <LockIcon className="w-4 h-4 text-slate-300 group-hover:text-slate-400 transition-colors" data-id="element-2981" data-mp="2882rkmoftdzb" />
              </button>;
        }
        return <NavLink key={item.to} to={item.to} onClick={item.to === '/app' ? e => {
          // Clicking Discover always starts a fresh chat,
          // even when already on the Discover route.
          e.preventDefault();
          navigate('/app', {
            state: {
              reset: Date.now()
            }
          });
        } : undefined} className={({
          isActive
        }) => `relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${item.badge ? 'bg-gradient-to-r from-primary-indigo/10 to-ai-purple/10 text-primary-indigo font-medium ring-1 ring-primary-indigo/30 animate-pulse' : isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-2982" data-mp="23b2kc8fuq56m">
              {({
            isActive
          }) => <>
                  <item.icon className={`w-5 h-5 ${isActive || item.badge ? 'text-primary-indigo' : ''}`} data-id="element-2983" data-mp="10fm218jovsd9" />
                  <span className="flex-1" data-id="element-2984" data-mp="2bvkfrpptb2kt">{item.label}</span>
                  {item.badge && <span className="text-[10px] font-bold uppercase tracking-wide bg-gradient-to-r from-primary-indigo to-ai-purple text-white px-2 py-0.5 rounded-full shadow-sm shadow-primary-indigo/40" data-id="element-2985" data-mp="2gun94cwfjkcy">
                      New
                    </span>}
                </>}
            </NavLink>;
      })}
      </nav>

      {/* Help — pinned to the bottom */}
      <div className="px-4 pb-4 pt-2 border-t border-slate-100 mt-2" data-id="element-2986" data-mp="2kct31kqsvp1b">
        {!isAuthenticated ? <button onClick={openLogin} title="Sign in to access" className="group relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2987" data-mp="2yc2ksounmr7e">
            <LifeBuoyIcon className="w-5 h-5" data-id="element-2988" data-mp="380u82jorsnbz" />
            <span className="flex-1 text-left" data-id="element-2989" data-mp="3332dtj0nkp4m">Help</span>
            <LockIcon className="w-4 h-4 text-slate-300 group-hover:text-slate-400 transition-colors" data-id="element-2990" data-mp="333klrcwogrsd" />
          </button> : <NavLink to="/help" className={({
        isActive
      }) => `relative flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-2991" data-mp="3c3qu6kdir1ko">
            {({
          isActive
        }) => <>
                <LifeBuoyIcon className={`w-5 h-5 ${isActive ? 'text-primary-indigo' : ''}`} data-id="element-2992" data-mp="3mokubn9ehbfx" />
                <span className="flex-1" data-id="element-2993" data-mp="3cz194ay7t5hw">Help</span>
              </>}
          </NavLink>}
      </div>
    </aside>;
}